import { useState, useEffect } from "react";
import { usersApi, billingsApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, DollarSign, Edit, TrendingUp, TrendingDown } from "lucide-react";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - 3 + i);

const PartnerBillings = () => {
  const [loading, setLoading] = useState(true);
  const [billers, setBillers] = useState([]);
  const [allBillings, setAllBillings] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [prevYearSummary, setPrevYearSummary] = useState(null);

  const [formData, setFormData] = useState({
    user_id: "",
    year: currentYear,
    month: new Date().getMonth() + 1,
    client_billings: "",
  });

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonth]);

  const loadData = async () => {
    try {
      const actualCurrentYear = new Date().getFullYear();
      
      // For YTD comparison logic:
      // - If viewing CURRENT year YTD: compare to completed months of previous year only
      // - If viewing PAST year YTD: compare full year to full year
      // - If viewing specific month: compare that month to same month previous year
      let ytdMaxMonth = null;
      if (!selectedMonth && selectedYear === actualCurrentYear) {
        // Viewing current year YTD - only compare completed months
        const currentMonth = new Date().getMonth() + 1; // 1-12
        const completedMonths = currentMonth - 1;
        ytdMaxMonth = completedMonths > 0 ? completedMonths : null;
      }
      
      const [billersRes, billingsRes, summaryRes, prevSummaryRes] = await Promise.all([
        usersApi.getBillers(),
        billingsApi.getAll(),
        billingsApi.getSummary(selectedYear, selectedMonth, null),
        billingsApi.getSummary(selectedYear - 1, selectedMonth, ytdMaxMonth),
      ]);
      setBillers(billersRes.data);
      setAllBillings(billingsRes.data);
      setSummary(summaryRes.data);
      setPrevYearSummary(prevSummaryRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        client_billings: parseFloat(formData.client_billings) || 0,
      };

      if (editMode && editingId) {
        await billingsApi.update(editingId, { client_billings: payload.client_billings });
        toast.success("Billing updated successfully");
      } else {
        await billingsApi.create(payload);
        toast.success("Billing added successfully");
      }

      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save billing");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (billing) => {
    setFormData({
      user_id: billing.user_id,
      year: billing.year,
      month: billing.month,
      client_billings: billing.client_billings.toString(),
    });
    setEditMode(true);
    setEditingId(billing.id);
    setDialogOpen(true);
  };

  const handleDelete = async (billingId) => {
    try {
      await billingsApi.delete(billingId);
      toast.success("Billing deleted successfully");
      loadData();
    } catch (error) {
      toast.error("Failed to delete billing");
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      year: currentYear,
      month: new Date().getMonth() + 1,
      client_billings: "",
    });
    setEditMode(false);
    setEditingId(null);
  };

  const getBillerName = (userId) => {
    const biller = billers.find((b) => b.id === userId);
    return biller?.name || "Unknown";
  };

  const filteredBillings = allBillings
    .filter((b) => {
      const matchesYear = b.year === selectedYear;
      const matchesMonth = selectedMonth ? b.month === selectedMonth : true;
      return matchesYear && matchesMonth;
    })
    .sort((a, b) => {
      // Sort by month descending, then by name
      if (b.month !== a.month) return b.month - a.month;
      return getBillerName(a.user_id).localeCompare(getBillerName(b.user_id));
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-billings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Partner Billings</h1>
          <p className="text-muted-foreground">Track monthly client billings by partner</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-billing-btn">
              <Plus size={18} />
              Add Billing
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editMode ? "Edit Billing" : "Add Partner Billing"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="user_id">Partner / Biller</Label>
                <Select
                  value={formData.user_id}
                  onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                  disabled={editMode}
                >
                  <SelectTrigger data-testid="select-biller">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...billers].sort((a, b) => a.name.localeCompare(b.name)).map((biller) => (
                      <SelectItem key={biller.id} value={biller.id}>
                        {biller.name} ({biller.position})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="month">Month</Label>
                  <Select
                    value={formData.month.toString()}
                    onValueChange={(value) => setFormData({ ...formData, month: parseInt(value) })}
                    disabled={editMode}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m) => (
                        <SelectItem key={m.value} value={m.value.toString()}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="year">Year</Label>
                  <Select
                    value={formData.year.toString()}
                    onValueChange={(value) => setFormData({ ...formData, year: parseInt(value) })}
                    disabled={editMode}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {YEARS.map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_billings">Client Billings ($)</Label>
                <Input
                  id="client_billings"
                  type="number"
                  step="0.01"
                  value={formData.client_billings}
                  onChange={(e) => setFormData({ ...formData, client_billings: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="input-client-billings"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting || !formData.user_id}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editMode ? "Update" : "Save"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm font-medium">
                Total Client Billings {selectedMonth ? MONTHS.find(m => m.value === selectedMonth)?.label : "YTD"} {selectedYear}
              </p>
              <p className="text-3xl font-bold mt-1">
                ${summary?.total_billings?.toLocaleString() || "0.00"}
              </p>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <TrendingUp size={28} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign size={20} />
            Billing Records
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="w-32">
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger data-testid="year-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Select 
                value={selectedMonth?.toString() || "all"} 
                onValueChange={(v) => setSelectedMonth(v === "all" ? null : parseInt(v))}
              >
                <SelectTrigger data-testid="month-filter">
                  <SelectValue placeholder="All Months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {billers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="mx-auto h-12 w-12 text-muted-foreground/50 mb-2" />
              <p>No billing partners configured.</p>
              <p className="text-sm">Update user settings to enable billing tracking.</p>
            </div>
          ) : filteredBillings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="mx-auto h-12 w-12 text-muted-foreground/50 mb-2" />
              <p>No billing records for this period.</p>
              <p className="text-sm">Click "Add Billing" to enter data.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="billings-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Partner</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Client Billings</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBillings.map((billing) => (
                    <TableRow key={billing.id}>
                      <TableCell className="font-medium">{getBillerName(billing.user_id)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {MONTHS.find((m) => m.value === billing.month)?.label} {billing.year}
                      </TableCell>
                      <TableCell className="text-right font-mono text-emerald-500 font-semibold">
                        ${billing.client_billings.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(billing)}
                            className="text-muted-foreground hover:text-blue-500"
                            data-testid={`edit-billing-${billing.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground hover:text-rose-600"
                                data-testid={`delete-billing-${billing.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Billing Record</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this billing record for {getBillerName(billing.user_id)}?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(billing.id)}
                                  className="bg-rose-600 hover:bg-rose-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Partner Summary */}
      {summary?.by_partner && Object.keys(summary.by_partner).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">By Partner</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(summary.by_partner)
                .sort((a, b) => a[1].name.localeCompare(b[1].name))
                .map(([userId, data]) => {
                  const prevData = prevYearSummary?.by_partner?.[userId];
                  const prevTotal = prevData?.total || 0;
                  const currentTotal = data.total;
                  const change = currentTotal - prevTotal;
                  const percentChange = prevTotal > 0 ? ((change / prevTotal) * 100).toFixed(1) : null;
                  const isUp = change >= 0;
                  
                  return (
                    <div key={userId} className="bg-muted rounded-lg p-4">
                      <p className="font-medium text-foreground">{data.name}</p>
                      <p className="text-2xl font-bold text-emerald-500">${data.total.toLocaleString()}</p>
                      {prevTotal > 0 ? (
                        <p className={`text-sm mt-1 ${isUp ? 'text-emerald-500' : 'text-rose-600'}`}>
                          {isUp ? '↑' : '↓'} {isUp ? '+' : ''}{percentChange}% vs {selectedYear - 1}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">No {selectedYear - 1} data</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{data.entries.length} entries</p>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PartnerBillings;
